import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Video, Monitor, Palette, ArrowRight, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ServicesSection = ({ whatsappUrl }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { language } = useLanguage();

  const content = {
    en: {
      overline: 'What We Do',
      headline: 'Services That Drive',
      headlineAccent: 'Growth',
      cta: 'Discuss Your Project',
      services: [
        {
          icon: Target,
          title: 'Meta Ads Management',
          description: 'Data-driven Instagram & Facebook campaigns that scale your business predictably.',
        },
        {
          icon: Video,
          title: 'AI Video Production',
          description: 'High-conversion video content powered by cutting-edge AI technology.',
        },
        {
          icon: Monitor,
          title: 'Website Design',
          description: 'Modern, high-converting websites that bring customers, not just visitors.',
        },
        {
          icon: Palette,
          title: 'Brand & Content',
          description: 'Premium brand identity and content strategy that commands attention.',
        },
      ],
    },
    tr: {
      overline: 'Ne Yapıyoruz',
      headline: 'Büyüme Sağlayan',
      headlineAccent: 'Hizmetler',
      cta: 'Projenizi Tartışalım',
      services: [
        {
          icon: Target,
          title: 'Meta Reklam Yönetimi',
          description: 'İşletmenizi öngörülebilir şekilde büyüten veri odaklı Instagram & Facebook kampanyaları.',
        },
        {
          icon: Video,
          title: 'AI Video Prodüksiyonu',
          description: 'Son teknoloji yapay zeka ile güçlendirilmiş yüksek dönüşümlü video içerikleri.',
        },
        {
          icon: Monitor,
          title: 'Web Sitesi Tasarımı',
          description: 'Ziyaretçi değil, müşteri getiren modern, yüksek dönüşümlü web siteleri.',
        },
        {
          icon: Palette,
          title: 'Marka & İçerik',
          description: 'Dikkat çeken premium marka kimliği ve içerik stratejisi.',
        },
      ],
    },
  };

  const t = content[language] || content.en;

  return (
    <section id="services" className="py-16 md:py-20 bg-[#F9FAFB]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-[#E60000] text-sm uppercase tracking-[0.2em] font-bold">{t.overline}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mt-3 tracking-tight">
            {t.headline} <span className="text-[#E60000]">{t.headlineAccent}</span>
          </h2>
        </motion.div>

        {/* Services Grid - 4 columns on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:border-[#E60000]/20 transition-all duration-300 hover:-translate-y-1"
              data-testid={`service-card-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#E60000]/10 flex items-center justify-center mb-4 group-hover:bg-[#E60000] transition-colors duration-300">
                <service.icon className="w-6 h-6 text-[#E60000] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2">
                {service.title}
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <motion.a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#E60000] text-white px-7 py-3.5 rounded-full font-bold shadow-lg hover:bg-[#CC0000] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="services-whatsapp-btn"
          >
            <MessageCircle size={18} />
            {t.cta}
            <ArrowRight size={16} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
